import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { toPng } from 'html-to-image';
import { flattenLinearLightForCapture } from './capture';
import { getLinearLightFontEmbedCSS } from './fonts';
import {
  loadLinearLightImage,
  copyLinearLightPresentation,
  LinearLightLayoutPending,
} from './source';

// WebKit accepts the CSS syntax but does not paint SVG backdrop references.
// Keep the live Chromium compositor; only this demonstrated engine gap needs
// an explicit background input. iOS browsers also use WebKit.
export function needsExplicitBackdrop() {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  return /AppleWebKit/.test(ua) && !/Chrome\/|Chromium\/|Edg\/|OPR\//.test(ua);
}

export function excludedBackdropElements(
  root: HTMLElement,
  layer: HTMLElement,
  elementId?: string
) {
  const excluded = new Set<Element>([layer]);
  let branch: Element = layer;
  while (root.contains(branch) && branch !== root && branch.parentElement) {
    const siblings = Array.from(branch.parentElement.children);
    siblings
      .slice(siblings.indexOf(branch) + 1)
      .forEach((node) => excluded.add(node));
    branch = branch.parentElement;
  }
  // Crop paint lives in the Edit layer. Its Display counterpart is omitted;
  // use the authored order to exclude the remaining higher Display layers.
  const order = root.closest<HTMLElement>('[data-linear-light-elements]')
    ?.dataset.linearLightElements;
  if (elementId && order) {
    const ids: string[] = JSON.parse(order);
    const index = ids.indexOf(elementId);
    if (index >= 0)
      root
        .querySelectorAll<HTMLElement>('[data-element-id]')
        .forEach((node) => {
          if (ids.indexOf(node.dataset.elementId || '') >= index)
            excluded.add(node.closest('[data-linear-light-layer]') || node);
        });
  }
  return excluded;
}

function pageForLayer(layer: HTMLElement) {
  const edit = layer.closest('[data-testid="editLayer"]');
  return (
    ((edit
      ? edit.parentElement?.querySelector(
          '[data-testid="DisplayLayer"] [data-testid="fullbleed"]'
        )
      : layer.closest(
          '[data-testid="fullbleed"],[data-linear-light-page]'
        )) as HTMLElement | null) || layer.parentElement
  );
}

// The snapshot belongs to one layer/page. Target-only dragging repositions the
// existing background image; it does not capture the page on pointer movement.
// Underlying paint changes and live video/animations request a fresh image.
export default function useBackdrop({
  enabled,
  source,
  effect,
  image,
  elementId,
  onError,
}: {
  enabled: boolean;
  source: RefObject<HTMLElement>;
  effect: RefObject<HTMLElement | SVGSVGElement>;
  image: RefObject<SVGFEImageElement>;
  elementId?: string;
  onError?: (error: Error) => void;
}) {
  const [capture, setCapture] = useState({ uri: '', failed: false });
  const errorRef = useRef(onError);
  errorRef.current = onError;
  useLayoutEffect(() => {
    if (!enabled || !source.current?.parentElement) return;
    setCapture({ uri: '', failed: false });
    const layer = source.current.parentElement;
    const root = pageForLayer(layer);
    if (!root) return;
    let active = true;
    let busy = false;
    let dirty = true;
    let frame = 0;
    let failed = false;
    const within = (node: Node, excluded: Set<Element>) =>
      Array.from(excluded).some((item) => item === node || item.contains(node));
    const align = () => {
      const target = effect.current;
      const input = image.current;
      if (!target || !input) return;
      const page = root.getBoundingClientRect();
      const box = target.getBoundingClientRect();
      if (!page.width || !page.height) return;
      const attributes = {
        x: ((page.left - box.left) * root.offsetWidth) / page.width,
        y: ((page.top - box.top) * root.offsetHeight) / page.height,
        width: root.offsetWidth,
        height: root.offsetHeight,
      };
      Object.entries(attributes).forEach(([name, value]) => {
        if (input.getAttribute(name) !== String(value))
          input.setAttribute(name, String(value));
      });
    };
    const schedule = () => {
      if (active && !frame && !busy) frame = requestAnimationFrame(run);
    };
    const change = () => {
      dirty = true;
      failed = false;
      align();
      schedule();
    };
    async function run() {
      frame = 0;
      if (!active || busy) return;
      const excluded = excludedBackdropElements(root, layer, elementId);
      const moving = () =>
        Array.from(root.querySelectorAll('video')).some(
          (video) => !within(video, excluded) && !video.paused && !video.ended
        ) ||
        (root.getAnimations?.({ subtree: true }) || []).some((animation) => {
          const target = (animation.effect as KeyframeEffect | null)?.target;
          return (
            animation.playState === 'running' &&
            target &&
            !within(target, excluded)
          );
        });
      const wasMoving = moving();
      if (!dirty && !wasMoving) return;
      const width = root.offsetWidth;
      const height = root.offsetHeight;
      if (!width || !height) return;
      dirty = false;
      busy = true;
      try {
        const fontEmbedCSS = await getLinearLightFontEmbedCSS(root);
        if (!active) return;
        if (
          Array.from(
            root.querySelectorAll('[data-linear-light-status="loading"]')
          ).some((node) => !within(node, excluded))
        )
          throw new LinearLightLayoutPending('Lower layer is still loading.');
        const snapshot = copyLinearLightPresentation(
          root,
          (node) => !excluded.has(node)
        ) as HTMLElement;
        const buffer = document.createElement('div');
        Object.assign(buffer.style, {
          position: 'fixed',
          left: '-100000px',
          top: '0',
          width: `${width}px`,
          height: `${height}px`,
          opacity: '0',
          pointerEvents: 'none',
        });
        Object.assign(snapshot.style, {
          position: 'relative',
          left: '0',
          top: '0',
          margin: '0',
          width: `${width}px`,
          height: `${height}px`,
        });
        buffer.append(snapshot);
        document.body.append(buffer);
        let next: string;
        try {
          // SVG-as-image capture omits CSS backdrop references. Flatten lower
          // compositor layers in this owned copy before sampling them.
          if (
            Array.from(
              snapshot.querySelectorAll('[data-linear-light-layer]')
            ).some(
              (node) => !node.querySelector('[data-linear-light-explicit]')
            )
          )
            await flattenLinearLightForCapture(snapshot, { fontEmbedCSS });
          next = await toPng(snapshot, {
            fontEmbedCSS,
            width,
            height,
          });
        } finally {
          buffer.remove();
        }

        await loadLinearLightImage(next);
        if (!active) return;
        if (width !== root.offsetWidth || height !== root.offsetHeight) {
          dirty = true;
          return;
        }
        setCapture({ uri: next, failed: false });
        align();
      } catch (error) {
        if (error instanceof LinearLightLayoutPending) {
          dirty = true;
          return;
        }
        failed = true;
        if (active) setCapture({ uri: '', failed: true });
        if (active)
          errorRef.current?.(new Error('Unable to render layer backdrop.'));
      } finally {
        busy = false;
        // An animation can finish while its snapshot is encoding. Take one
        // final stable frame instead of retaining its penultimate appearance.
        if (wasMoving) dirty = true;
        if (active && !failed && (dirty || moving())) schedule();
      }
    }
    const observer = new MutationObserver((records) => {
      align();
      const excluded = excludedBackdropElements(root, layer, elementId);
      if (records.some((record) => !within(record.target, excluded))) change();
    });
    observer.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      characterData: true,
    });
    // The Edit layer's movement is outside the Display page snapshot.
    const movement = new MutationObserver(align);
    if (!root.contains(layer))
      movement.observe(layer, { subtree: true, attributes: true });
    const resize = new ResizeObserver(change);
    resize.observe(root);
    const events = ['loadeddata', 'seeked', 'play', 'pause', 'ended'];
    events.forEach((name) => root.addEventListener(name, change, true));
    const animationChange = (event: Event) => {
      const excluded = excludedBackdropElements(root, layer, elementId);
      if (event.target instanceof Node && !within(event.target, excluded))
        change();
    };
    root.addEventListener('web-stories-animation-update', animationChange);
    document.fonts.addEventListener('loadingdone', change);
    change();
    return () => {
      active = false;
      cancelAnimationFrame(frame);
      observer.disconnect();
      movement.disconnect();
      resize.disconnect();
      events.forEach((name) => root.removeEventListener(name, change, true));
      root.removeEventListener('web-stories-animation-update', animationChange);
      document.fonts.removeEventListener('loadingdone', change);
    };
  }, [enabled, source, effect, image, elementId]);
  return enabled ? capture : { uri: '', failed: false };
}
